import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { UsersApi, type NetworkUser, type RoleRef } from '@/services/users/usersApi'

interface CopyUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: NetworkUser
  /** The roles the copy starts with: the user's roles in the current network. */
  roles: RoleRef[]
  networkId: string
  /** Where a user's page lives, so the copy can be opened. */
  basePath: string
}

/** Creates a new user with another user's settings and roles in this network. */
export function CopyUserDialog({
  open,
  onOpenChange,
  user,
  roles,
  networkId,
  basePath,
}: CopyUserDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [username, setUsername] = useState('')
  const [name, setName] = useState(user.name)
  const [displayName, setDisplayName] = useState(user.displayName)
  const [openCopy, setOpenCopy] = useState(true)

  const mutation = useMutation({
    mutationFn: () => {
      const id = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
      return UsersApi.save(
        id,
        {
          username: username.trim(),
          name: name.trim(),
          surname: user.surname,
          displayName: displayName.trim(),
          locale: user.locale,
          preferredCurrency: user.preferredCurrency,
          objectState: 'ACTIVE',
        },
        networkId,
        roles
      )
    },
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(t('copyUser.copied'), {
        description: t('copyUser.copiedDetail', { username: copy.username }),
        duration: 3000,
      })
      onOpenChange(false)
      if (openCopy) navigate(`${basePath}/${copy.id}`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t('copyUser.failed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const valid = username.trim() && name.trim() && displayName.trim()

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('copyUser.title')}</DialogTitle>
            <DialogDescription>
              {t('copyUser.description', { name: user.displayName || user.username })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-user-username">{t('users.email')}</FieldLabel>
            <Input
              id="copy-user-username"
              type="email"
              autoComplete="off"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-user-name">{t('common.name')}</FieldLabel>
            <Input
              id="copy-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-user-display-name">{t('users.displayName')}</FieldLabel>
            <Input
              id="copy-user-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="copy-user-open"
              checked={openCopy}
              onCheckedChange={(v) => setOpenCopy(v === true)}
            />
            <Label htmlFor="copy-user-open" className="font-normal">
              {t('copyUser.openCopy')}
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!valid}>
              <Copy className="size-4" />
              {t('copyUser.copy')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
